import { Fragment, useMemo } from 'react';

import { useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemButton,
  Typography,
} from '@mui/material';
import { Image } from '@realadvisor/image';
import { Building2, Search } from 'lucide-react';
import { Link, type To } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import type { GetBuyerLeadOtherListingsQuery } from '@graphql/__generated__/graphql';
import { GET_PROPERTY_TYPES } from '@modules/_shared/common/queries';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { formatAddress } from '@modules/_shared/utils/formatting';
import { getDefaultListingsMatchingFilters } from '@modules/users/components/UserBuyerLeadMatchingDrawer';
import { useLocale } from '@shared/hooks/locale';
import { toGlobalId } from '@shared/utils/global-id';

const GET_BUYER_LEAD_OTHER_LISTINGS = gql(/* GraphQL */ `
  query GetBuyerLeadOtherListings($userId: uuid!, $excludeId: uuid!) {
    buyer_leads(
      where: {
        user_id: { _eq: $userId }
        id: { _neq: $excludeId }
        lot_id: { _is_null: false }
      }
      order_by: { updated_at: desc }
      limit: 50
    ) {
      id
      updated_at
      lead_stage {
        id
        label
      }
      lot {
        id
        property {
          street_number
          route
          postcode
          locality
          state
          country
          property_images(order_by: { order_nr: asc }, limit: 1) {
            id
            image {
              id
              url
            }
          }
        }
      }
    }
    property_searches(
      where: { user_id: { _eq: $userId }, notification: { _neq: "never" } }
    ) {
      ...ListingsMatchingFiltersPropertySearches
    }
  }
`);

type OtherBuyerLead = NonNullable<
  GetBuyerLeadOtherListingsQuery['buyer_leads'][number]
>;

const OtherListingItem = ({ buyerLead }: { buyerLead: OtherBuyerLead }) => {
  const address = buyerLead.lot?.property
    ? formatAddress(buyerLead.lot.property, ', ')
    : null;
  const coverImageUrl =
    buyerLead.lot?.property?.property_images?.[0]?.image?.url ?? null;

  return (
    <ListItemButton
      component={Link}
      to={`/listings/${buyerLead.lot?.id}/buyers/${buyerLead.id}`}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ gap: 1.5, alignItems: 'center' }}
    >
      <Box
        sx={{
          width: 56,
          height: 42,
          flexShrink: 0,
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& img': { width: '100%', height: '100%', objectFit: 'cover' },
        }}
      >
        {coverImageUrl != null ? (
          <Image
            src={coverImageUrl}
            options={{ w: 56, h: 42, f: 'jpg', c: 'fill' }}
          />
        ) : (
          <Building2 size={20} style={{ opacity: 0.4 }} />
        )}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" noWrap>
          {address ?? '-'}
        </Typography>
        <Typography variant="body2" noWrap sx={{ color: 'text.secondary' }}>
          {buyerLead.lead_stage?.label ?? '-'}
        </Typography>
      </Box>
      <Typography
        variant="caption"
        sx={{ color: 'text.disabled', whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        <TimeAgo dateString={buyerLead.updated_at} />
      </Typography>
    </ListItemButton>
  );
};

type Props = {
  userId: string;
  currentBuyerLeadId: string;
  readOnly?: boolean;
};

export const BuyerLeadOtherListingsCard = ({
  userId,
  currentBuyerLeadId,
  readOnly = false,
}: Props) => {
  const { t } = useLocale();
  const { data } = useQuery(GET_BUYER_LEAD_OTHER_LISTINGS, {
    variables: { userId, excludeId: currentBuyerLeadId },
  });
  const { data: propertyTypesData } = useQuery(GET_PROPERTY_TYPES);

  const buyerLeads = (data?.buyer_leads ?? []).filter(
    buyerLead => buyerLead.lot?.id != null,
  );

  const matchingLink = useMemo<To>(() => {
    const params = getDefaultListingsMatchingFilters(
      data?.property_searches ?? [],
      propertyTypesData?.property_types ?? [],
    );
    return {
      pathname: `/v1/users/${toGlobalId('User', userId)}/buying/matching`,
      search: params.toString(),
    };
  }, [data?.property_searches, propertyTypesData?.property_types, userId]);

  return (
    <ListingAccordion
      title={
        buyerLeads.length > 0
          ? `${t('Other listings')} (${buyerLeads.length})`
          : t('Other listings')
      }
      defaultExpanded
      disablePadding
    >
      <List disablePadding>
        {buyerLeads.map((buyerLead, index) => (
          <Fragment key={buyerLead.id}>
            {index > 0 && <Divider component="li" />}
            <OtherListingItem buyerLead={buyerLead} />
          </Fragment>
        ))}
        <ListItem sx={{ py: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            disabled={readOnly}
            startIcon={<Search />}
            component={Link}
            to={matchingLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('Find matching listings')}
          </Button>
        </ListItem>
      </List>
    </ListingAccordion>
  );
};
